/**
 * Intrinsic size of `lockup-horizontal.png`, used to derive the rendered height
 * from `size` (which is a WIDTH for this orientation).
 *
 * Hardcoded rather than measured: an `<img>` reports nothing until it loads, so
 * deriving it at runtime would mean a first paint at the wrong height and a
 * layout shift. If the asset is ever regenerated at a different aspect, update
 * this — the co-located test pins the rendered ratio against these numbers.
 */
const HORIZONTAL_INTRINSIC_WIDTH = 1200;
const HORIZONTAL_INTRINSIC_HEIGHT = 289;

interface OroLockupBaseProps {
  /** For 'stacked', the rendered width AND height (the artwork is square).
   *  For 'horizontal', the rendered WIDTH — height follows the artwork aspect. */
  size?: number;
  className?: string;
  /** Hide from the a11y tree when a visible wordmark sits alongside it. */
  decorative?: boolean;
}

type OroLockupProps = OroLockupBaseProps &
  (
    | {
        /** Mark above the wordmark, square. The default. */
        orientation?: 'stacked';
        /** 'dark' renders the lockup on its dark plate;
         *  'transparent' for placing over an existing dark surface without a visible plate.
         *  Pick 'transparent' whenever the surface behind it is a FLAT colour: the plated
         *  artwork bakes a radial gradient that reads as a lighter square patch against one. */
        variant?: 'dark' | 'transparent';
      }
    | {
        /** Mark left of the wordmark, wide. Ships transparent-only, so `variant`
         *  is not accepted — there is no plated horizontal master to select. */
        orientation: 'horizontal';
        variant?: never;
      }
  );

/**
 * ORO move brand lockup. Two orientations, both baked PNGs regenerated from
 * `brand/source/lockup/` by `scripts/generate-brand-assets.mjs`:
 *
 *   - `stacked` (default) — three-dot mark ABOVE the wordmark, square (#2449).
 *   - `horizontal` — mark LEFT of the wordmark. What the login modal renders
 *     (brand-owner call, 2026-07-29 — #2523). Mirrors the native twin, which
 *     took the same call for the native login screen a day earlier (#2516).
 *
 * SIZING ASYMMETRY, on purpose: the stacked assets keep their master's padding,
 * so `size` is a square box the artwork sits inside. The horizontal asset is
 * TRIMMED to its ink, so `size` is the logo width itself. A given `size`
 * therefore renders a visibly larger horizontal logo than stacked; that is the
 * intent, not a bug. Kept identical to native's rule so the two APIs agree.
 *
 * For the mark alone use `OroMark`; for the inline text wordmark use
 * `OroTrademark`.
 *
 * Note the stacked transparent PNG is not a straight copy of its master: the
 * master is the light-background export, so the generator recolors its "move"
 * wordmark to the dark-variant grey. Don't "simplify" that step away — see the
 * generator's comment.
 *
 * The accessible name is the bare "ORO move" (no ™) — CLAUDE.md Rule 24's logo/alt
 * carve-out: a screen reader would read "™" aloud as "trademark", and the artwork
 * carries no ™. The visible text wordmark (`OroTrademark`) still keeps ™.
 */
export default function OroLockup({
  orientation = 'stacked',
  variant = 'dark',
  size = 200,
  className,
  decorative = false,
}: OroLockupProps) {
  const horizontal = orientation === 'horizontal';

  const src = horizontal
    ? '/images/brand/lockup-horizontal.png'
    : variant === 'transparent'
      ? '/images/brand/lockup-stacked-transparent.png'
      : '/images/brand/lockup-stacked-dark.png';

  // Square for stacked; aspect-derived for horizontal, so the reserved box
  // matches the artwork and the logo doesn't jump on load.
  const height = horizontal
    ? Math.round((size * HORIZONTAL_INTRINSIC_HEIGHT) / HORIZONTAL_INTRINSIC_WIDTH)
    : size;

  return (
    <img
      src={src}
      width={size}
      height={height}
      className={className}
      {...(decorative ? { alt: '', 'aria-hidden': true } : { alt: 'ORO move' })}
    />
  );
}
